import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';

interface ActivityLog {
  id: string;
  user_id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export default function AdminActivityPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout>>();
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3000);
  };

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const fetchActivities = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(pagination.page),
      limit: String(pagination.limit),
    });
    if (filterAction) params.set('action', filterAction);
    if (filterUserId) params.set('userId', filterUserId);
    if (filterDateFrom) params.set('dateFrom', filterDateFrom);
    if (filterDateTo) params.set('dateTo', filterDateTo);

    fetch(`/api/admin/activity?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
      },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.success) {
          setActivities(res.data.activities);
          setPagination({
            page: res.data.page,
            totalPages: Math.ceil(res.data.total / res.data.limit),
            total: res.data.total,
            limit: res.data.limit,
          });
        } else {
          setActivities([]);
        }
      })
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.limit, filterAction, filterUserId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchActivities();
  }, [isAuthenticated, navigate, fetchActivities]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterAction) params.set('action', filterAction);
      if (filterUserId) params.set('userId', filterUserId);
      if (filterDateFrom) params.set('dateFrom', filterDateFrom);
      if (filterDateTo) params.set('dateTo', filterDateTo);
      params.set('limit', '10000');

      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/activity/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Ошибка экспорта');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_logs_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast('Ошибка экспорта: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((p) => ({ ...p, page: 1 }));
    fetchActivities();
  };

  const actionLabel = (action: string) => {
    const labels: Record<string, string> = {
      login: 'Вход',
      logout: 'Выход',
      profile_update: 'Обновление профиля',
      admin_password_reset: 'Сброс пароля',
      trial_activated: 'Активация триала',
      trial_extended: 'Продление триала',
      trial_converted: 'Конвертация триала',
    };
    return labels[action] || action;
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {toast && <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{toast.message}</div>}
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/admin/dashboard" className="text-xl font-bold text-white hover:text-purple-400 transition">
            ← Назад
          </Link>
          <h1 className="text-xl font-bold text-white">Логи активности ({pagination.total})</h1>
          <button
            onClick={handleExportCsv}
            disabled={exporting}
            className="px-4 py-2 text-sm bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/40 transition disabled:opacity-50"
          >
            {exporting ? 'Экспорт...' : 'Экспорт в CSV'}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <form onSubmit={handleFilterSubmit} className="mb-6 flex flex-wrap gap-3">
          <input
            type="text"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            placeholder="Действие"
            className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <input
            type="text"
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            placeholder="ID пользователя"
            className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            Применить
          </button>
        </form>

        {/* Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="px-6 py-12 text-center text-slate-500">Загрузка...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                      <th className="px-6 py-3 font-medium">Дата</th>
                      <th className="px-6 py-3 font-medium">Пользователь</th>
                      <th className="px-6 py-3 font-medium">Действие</th>
                      <th className="px-6 py-3 font-medium">Ресурс</th>
                      <th className="px-6 py-3 font-medium">Детали</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                          Логи не найдены
                        </td>
                      </tr>
                    ) : (
                      activities.map((log) => (
                        <tr key={log.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition">
                          <td className="px-6 py-3 text-slate-300 text-sm">
                            {new Date(log.created_at).toLocaleString('ru-RU')}
                          </td>
                          <td className="px-6 py-3">
                            <Link
                              to={`/admin/users/${log.user_id}`}
                              className="text-blue-400 hover:underline text-sm"
                            >
                              {log.user_id.substring(0, 8)}...
                            </Link>
                          </td>
                          <td className="px-6 py-3">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300">
                              {actionLabel(log.action)}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-slate-400 text-sm">
                            {log.resource_type || '—'}
                          </td>
                          <td className="px-6 py-3 text-slate-400 text-xs max-w-xs truncate">
                            {log.details ? JSON.stringify(log.details).substring(0, 100) : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-sm text-slate-400">
                    Страница {pagination.page} из {pagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition"
                    >
                      ←
                    </button>
                    <button
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition"
                    >
                      →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
